/**
 * Breadcrumb configuration for the application
 *
 * IMPORTANT: This configuration ONLY includes routes that actually exist.
 * Routes are verified against the actual router configurations in:
 * - src/App.tsx (main routes)
 * - src/router/* (sub-routers)
 *
 * Each route pattern maps to a breadcrumb trail that represents the ACTUAL
 * navigation hierarchy, not an assumed one.
 */

export interface BreadcrumbItem {
  label: string;
  path: string;
}

interface RoutePattern {
  pattern: RegExp;
  getBreadcrumbs: (
    pathname: string,
    matches: RegExpMatchArray,
  ) => BreadcrumbItem[];
}

/**
 * Route patterns and their corresponding breadcrumb configurations.
 * Patterns are matched in order, so more specific patterns should come first.
 *
 * KEY NOTES:
 * - /reports does NOT have an index route (404)
 * - /organizations does NOT have an index route (404)
 * - /memberships does NOT have an index route (404)
 * - Tenant reports are under /tenants/reports/:id
 * - Organization has subroutes: /organizations/members, /organizations/reports, etc.
 * - The contract editor routes (CONTRACT_EDITOR_PATHS) render under FullScreenLayout,
 *   which has no Header, so they have no breadcrumbs here
 */
const routePatterns: RoutePattern[] = [
  // ============================================================================
  // TENANT ROUTES (/tenants/*)
  // ============================================================================
  {
    pattern: /^\/tenants\/reports\/([^/]+)$/,
    getBreadcrumbs: (pathname) => [
      { label: "Reportes", path: "/" },
      { label: "Detalle de Reporte", path: pathname },
    ],
  },
  {
    pattern: /^\/tenants$/,
    getBreadcrumbs: () => [{ label: "Inquilinos", path: "/tenants" }],
  },

  // ============================================================================
  // PROPERTY ROUTES (/properties/*)
  // ============================================================================
  {
    pattern: /^\/properties\/([^/]+)\/details$/,
    getBreadcrumbs: (pathname) => [
      { label: "Inmuebles", path: "/properties" },
      { label: "Detalle", path: pathname },
    ],
  },
  {
    pattern: /^\/properties\/create$/,
    getBreadcrumbs: () => [
      { label: "Inmuebles", path: "/properties" },
      { label: "Crear", path: "/properties/create" },
    ],
  },
  {
    pattern: /^\/properties$/,
    getBreadcrumbs: () => [{ label: "Inmuebles", path: "/properties" }],
  },

  // ============================================================================
  // LANDLORD ROUTES (/landlords/*)
  // ============================================================================
  {
    pattern: /^\/landlords$/,
    getBreadcrumbs: () => [{ label: "Propietarios", path: "/landlords" }],
  },

  // ============================================================================
  // CONTRACT ROUTES (/contracts/*)
  // ============================================================================
  {
    pattern: /^\/contracts\/([^/]+)\/details$/,
    getBreadcrumbs: (pathname) => [
      { label: "Contratos", path: "/contracts" },
      { label: "Detalles", path: pathname },
    ],
  },
  {
    pattern: /^\/contracts\/success$/,
    getBreadcrumbs: () => [
      { label: "Contratos", path: "/contracts" },
      { label: "Éxito", path: "/contracts/success" },
    ],
  },
  {
    pattern: /^\/contracts$/,
    getBreadcrumbs: () => [{ label: "Contratos", path: "/contracts" }],
  },

  // ============================================================================
  // REPORT ROUTES (/reports/*)
  // WARNING: /reports itself does NOT exist (no index route)
  // ============================================================================
  {
    pattern: /^\/reports\/multireport\/([^/]+)$/,
    getBreadcrumbs: (pathname) => [{ label: "Multireporte", path: pathname }],
  },
  {
    pattern: /^\/reports\/multireport$/,
    getBreadcrumbs: () => [
      { label: "Multireporte", path: "/reports/multireport" },
    ],
  },
  {
    pattern: /^\/reports\/([^/]+)$/,
    getBreadcrumbs: (pathname) => [
      { label: "Reporte Express", path: pathname },
    ],
  },

  // ============================================================================
  // ORGANIZATION ROUTES (/organizations/*)
  // WARNING: /organizations itself does NOT exist (no index route)
  // Since there's no parent index, we show only the specific page name
  // ============================================================================
  {
    pattern: /^\/organizations\/create$/,
    getBreadcrumbs: () => [
      { label: "Crear Organización", path: "/organizations/create" },
    ],
  },
  {
    pattern: /^\/organizations\/members$/,
    getBreadcrumbs: () => [
      { label: "Asesores", path: "/organizations/members" },
    ],
  },
  {
    pattern: /^\/organizations\/quotas\/orders\/([^/]+)$/,
    getBreadcrumbs: (pathname) => [
      { label: "Cuotas", path: "/organizations/quotas" },
      { label: "Detalle de Orden", path: pathname },
    ],
  },
  {
    pattern: /^\/organizations\/quotas$/,
    getBreadcrumbs: () => [{ label: "Cuotas", path: "/organizations/quotas" }],
  },
  {
    pattern: /^\/organizations\/credits$/,
    getBreadcrumbs: () => [{ label: "Cuotas", path: "/organizations/credits" }],
  },
  {
    pattern: /^\/organizations\/tenants$/,
    getBreadcrumbs: () => [
      { label: "Inquilinos", path: "/organizations/tenants" },
    ],
  },
  {
    pattern: /^\/organizations\/properties\/([^/]+)\/details$/,
    getBreadcrumbs: () => [
      { label: "Inmuebles", path: "/organizations/properties" },
      { label: "Detalle", path: "" },
    ],
  },
  {
    pattern: /^\/organizations\/properties$/,
    getBreadcrumbs: () => [
      { label: "Inmuebles", path: "/organizations/properties" },
    ],
  },
  {
    pattern: /^\/organizations\/reports$/,
    getBreadcrumbs: () => [
      { label: "Reportes", path: "/organizations/reports" },
    ],
  },
  {
    pattern: /^\/organizations\/settings$/,
    getBreadcrumbs: () => [
      { label: "Configuración", path: "/organizations/settings" },
    ],
  },

  // ============================================================================
  // MEMBERSHIP ROUTES (/memberships/*)
  // WARNING: /memberships itself does NOT exist (no index route)
  // Since there's no parent index, we show only the specific page name
  // ============================================================================
  {
    pattern: /^\/memberships\/general$/,
    getBreadcrumbs: () => [{ label: "General", path: "/memberships/general" }],
  },
  {
    pattern: /^\/memberships\/config$/,
    getBreadcrumbs: () => [
      { label: "Configuración", path: "/memberships/config" },
    ],
  },
  {
    pattern: /^\/memberships\/pricing$/,
    getBreadcrumbs: () => [{ label: "Planes", path: "/memberships/pricing" }],
  },
  {
    pattern: /^\/memberships\/checkout$/,
    getBreadcrumbs: () => [{ label: "Pago", path: "/memberships/checkout" }],
  },

  // ============================================================================
  // SINGLE PAGE ROUTES
  // ============================================================================
  {
    pattern: /^\/developers$/,
    getBreadcrumbs: () => [{ label: "Integración API", path: "/developers" }],
  },
  {
    pattern: /^\/profile$/,
    getBreadcrumbs: () => [{ label: "Perfil", path: "/profile" }],
  },
  {
    pattern: /^\/change-password$/,
    getBreadcrumbs: () => [
      { label: "Cambiar Contraseña", path: "/change-password" },
    ],
  },

  // ============================================================================
  // DASHBOARD (ROOT)
  // ============================================================================
  {
    pattern: /^\/$/,
    getBreadcrumbs: () => [{ label: "Dashboard", path: "/" }],
  },
];

/**
 * Get breadcrumbs for a given pathname
 * @param pathname - The current route pathname
 * @returns Array of breadcrumb items
 */
export const getBreadcrumbsForRoute = (pathname: string): BreadcrumbItem[] => {
  // Find the first matching pattern
  for (const route of routePatterns) {
    const matches = pathname.match(route.pattern);
    if (matches) {
      return route.getBreadcrumbs(pathname, matches);
    }
  }

  // Fallback: return empty array for unknown routes (like auth pages)
  // This prevents showing misleading breadcrumbs
  return [];
};
